import { t } from '@lingui/macro'
import { Typography } from '@mui/material'
import { PatientManagerIndexDTO } from '@om1/aspen-api/codegen/client/models/PatientManagerIndexDTO'
import { InlineDropdown, InlineDropdownOption } from '@om1/platform-components'
import DOMPurify from 'dompurify'
import React from 'react'
import { useAssignedPatientManagers } from '../hooks/useAssignedPatientManagers'

/**
 * Sanitizes text content to prevent XSS attacks using DOMPurify
 */
const sanitizeText = (text: string | undefined | null): string => {
    if (!text) return ''
    return DOMPurify.sanitize(String(text), { ALLOWED_TAGS: [] })
}

export interface PatientManagerDropdownProps {
    /**
     * The current patient manager ID for single entity mode
     */
    currentManagerId?: string | null
    /**
     * Entity ID for single assignment mode
     */
    entityId?: string
    /**
     * Array of entity IDs for bulk assignment mode
     */
    selectedEntityIds?: string[]
    /**
     * Type of entity being assigned
     */
    entityType: 'patient' | 'candidate'
    /**
     * List of available patient managers
     */
    patientManagers: PatientManagerIndexDTO[]
    /**
     * Whether patient managers are loading
     */
    loading?: boolean
    /**
     * Callback after successful assignment
     */
    onAssignmentComplete?: () => void
    /**
     * Custom label for bulk assignment trigger
     */
    bulkTriggerLabel?: string
    /**
     * Render mode - 'inline' for table rows, 'button' for bulk action bar
     */
    variant?: 'inline' | 'button'
}

/**
 * PatientManagerDropdown - A reusable dropdown component for assigning patient managers
 * Supports both single entity and bulk assignment modes
 *
 * @example Single mode (in table row):
 * ```tsx
 * <PatientManagerDropdown
 *   currentManagerId={candidate.patientManagerId}
 *   entityId={candidate.id}
 *   entityType="candidate"
 *   patientManagers={patientManagers}
 *   onAssignmentComplete={() => refetch()}
 * />
 * ```
 *
 * @example Bulk mode (in action bar):
 * ```tsx
 * <PatientManagerDropdown
 *   selectedEntityIds={selectedCandidateIds}
 *   entityType="candidate"
 *   patientManagers={patientManagers}
 *   onAssignmentComplete={() => refetch()}
 *   variant="button"
 *   bulkTriggerLabel="Assign to Selected"
 * />
 * ```
 */
export const PatientManagerDropdown: React.FC<PatientManagerDropdownProps> = ({
    currentManagerId,
    entityId,
    selectedEntityIds,
    entityType,
    patientManagers,
    loading = false,
    onAssignmentComplete,
    bulkTriggerLabel,
    variant = 'inline'
}) => {
    const { assignManager, bulkAssignManager, loading: assignmentLoading } = useAssignedPatientManagers()

    const isBulkMode = selectedEntityIds && selectedEntityIds.length > 0
    const isLoading = loading || assignmentLoading

    // Convert patient managers to dropdown options
    const managerOptions: InlineDropdownOption<string | null>[] = [
        { value: null, label: t`No Patient Manager` },
        ...patientManagers.map((manager) => ({
            value: manager.id,
            label: `${manager.name}${manager.email ? ` (${manager.email})` : ''}`
        }))
    ]

    const selectedManager = patientManagers.find((pm) => pm.id === currentManagerId)

    const handleSelect = async (managerId: string | null) => {
        const managerObj = managerId ? patientManagers.find((pm) => pm.id === managerId) ?? null : null

        if (isBulkMode && selectedEntityIds) {
            // Bulk assignment
            await bulkAssignManager(selectedEntityIds, managerObj, entityType)
        } else if (entityId) {
            // Single assignment
            await assignManager(entityId, managerObj, entityType)
        }

        onAssignmentComplete?.()
    }

    // Render trigger based on variant and mode
    const renderTrigger = (value: string | null, isOpen: boolean) => {
        if (isBulkMode) {
            // Bulk mode trigger
            const count = selectedEntityIds?.length || 0
            const label = bulkTriggerLabel || t`Assign Patient Manager (${count} selected)`

            if (variant === 'button') {
                return (
                    <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-primary-main text-white rounded-md cursor-pointer hover:bg-primary-dark transition-colors'>
                        <Typography variant='body2' className='text-white font-medium'>
                            {label}
                        </Typography>
                        <span className={`text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                    </div>
                )
            }

            return (
                <div className='inline-flex items-center cursor-pointer hover:opacity-80'>
                    <Typography variant='body2' color='primary'>
                        {label}
                    </Typography>
                    <span className={`ml-1 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                </div>
            )
        }

        // Single mode trigger
        if (!value || !selectedManager) {
            return (
                <div className='inline-flex items-center cursor-pointer hover:opacity-80'>
                    <Typography variant='body2' color='text.secondary'>
                        {t`Select Patient Manager`}
                    </Typography>
                    <span className={`ml-1 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
                </div>
            )
        }

        return (
            <div className='inline-flex items-center gap-1 cursor-pointer hover:opacity-80'>
                <Typography variant='body2'>{sanitizeText(selectedManager.name)}</Typography>
                <div className={`w-2 h-2 rounded-full ${selectedManager.active ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={`ml-1 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
            </div>
        )
    }

    if (isLoading && !isBulkMode) {
        return <div>{t`Loading...`}</div>
    }

    return (
        <InlineDropdown
            value={isBulkMode ? null : currentManagerId ?? null}
            options={managerOptions}
            onSelect={handleSelect}
            loading={isLoading}
            renderTrigger={renderTrigger}
            filterOptions={() => true} // Show all options including current value
        />
    )
}

export default PatientManagerDropdown
